import * as Yup from 'yup';
import { differenceInYears, format as fnsFormat, isFuture } from 'date-fns';
import i18next from 'i18next';
import { AnyObject, Maybe } from 'yup';
import { getFormattedDateInPast } from './format';

export const phoneValidationUtils: {
  mobile: {
    regexWithPrefix: RegExp;
    regexNoPrefix: RegExp;
  };
  landline: {
    regexWithPrefix: RegExp;
    regexNoPrefix: RegExp;
  };
} = {
  mobile: {
    regexWithPrefix: /^\+33(?:06|07|6|7)[0-9]+$/,
    regexNoPrefix: /^(?:06|07|6|7)[0-9]+$/,
  },
  landline: {
    regexWithPrefix: /^\+33(?!0[67]|[67])(?!0[67]|[67])[0-9]+$/,
    regexNoPrefix: /^(?!0[67]|[67])[0-9]+$/,
  },
};

export const getMaxBirthDate = () => {
  return getFormattedDateInPast({ years: 18 });
};

export const getMinBirthDate = () => {
  return getFormattedDateInPast({ years: 120 });
};

Yup.addMethod<Yup.StringSchema>(Yup.string, 'dateFormat', function format(formats, dateSeparator, message) {
  // need to be string for custom error message
  return this.test('testFormat', message, (value: string | null | undefined): boolean => {
    if (value) {
      const date = value.split(dateSeparator);
      return (
        value === fnsFormat(new Date(parseInt(date[2], 10), parseInt(date[1], 10) - 1, parseInt(date[0], 10)), formats)
      );
    }
    return true;
  });
});

Yup.addMethod<Yup.StringSchema>(Yup.string, 'fullAge', function fullAge(years, dateSeparator, message) {
  // need to be string for custom error message
  return this.test('testAge', message, (value: string | null | undefined): boolean => {
    if (value) {
      const date = value.split(dateSeparator);
      return (
        differenceInYears(
          new Date(),
          new Date(parseInt(date[2], 10), parseInt(date[1], 10) - 1, parseInt(date[0], 10))
        ) >= years
      );
    }
    return true;
  });
});

Yup.addMethod<Yup.StringSchema>(Yup.string, 'notInFuture', function notInFuture(dateSeparator, message) {
  // need to be string for custom error message
  return this.test('testFuture', message, (value: string | null | undefined): boolean => {
    if (value) {
      const date = value.split(dateSeparator);
      return !isFuture(new Date(parseInt(date[2], 10), parseInt(date[1], 10) - 1, parseInt(date[0], 10)));
    }
    return true;
  });
});

Yup.addMethod(Yup.string, 'elderAge', function elderAge(years, message) {
  // need to be string for custom error message
  return this.test('testAge', message, function testAge(value) {
    if (value) {
      // Commented-out during linter swipe
      // const diff = differenceInYears(new Date(), new Date(value)) <= years;
    }
    return true;
  });
});

Yup.addMethod(Yup.string, 'mobilePhoneLength', function mobilePhoneLength() {
  return this.test('mobilePhoneLength', function mobilePhoneLength(value) {
    const { path, createError } = this;

    if (!value) return true;

    const hasDomesticPrefix = value.startsWith('+336') || value.startsWith('+337');
    const hasForeignPrefix = value.startsWith('+3306') || value.startsWith('+3307');

    // 8 is minimal phone length + 1 domestic region prefix (6|7) + 3 characters for international prefix (+33)
    if (hasDomesticPrefix && value.length < 8 + 1 + 3) {
      return createError({
        path,
        message: i18next.t('validation:minLength', { value: 9 }),
      });
    }

    // 8 is minimal phone length + 2 foreign region prefix (06|07) + 3 characters for international prefix (+33)
    if (hasForeignPrefix && value.length < 8 + 2 + 3) {
      return createError({
        path,
        message: i18next.t('validation:minLength', { value: 10 }),
      });
    }

    return true;
  });
});

Yup.addMethod(Yup.string, 'mobilePhoneLengthNoPrefix', function mobilePhoneLengthNoPrefix() {
  return this.test('mobilePhoneLengthNoPrefix', function mobilePhoneLengthNoPrefix(value) {
    const { path, createError } = this;

    if (!value) return true;

    const hasDomesticPrefix = value.startsWith('6') || value.startsWith('7');
    const hasForeignPrefix = value.startsWith('06') || value.startsWith('07');

    // 8 is minimal phone length + 1 domestic region prefix (6|7)
    if (hasDomesticPrefix && value.length < 8 + 1) {
      return createError({
        path,
        message: i18next.t('validation:minLength', { value: 9 }),
      });
    }

    // 8 is minimal phone length + 2 foreign region prefix (06|07)
    if (hasForeignPrefix && value.length < 8 + 2) {
      return createError({
        path,
        message: i18next.t('validation:minLength', { value: 10 }),
      });
    }

    return true;
  });
});

Yup.addMethod(Yup.string, 'landlinePhoneLength', function landlinePhoneLength() {
  return this.test('landlinePhoneLength', function landlinePhoneLength(value) {
    const { path, createError } = this;

    if (!value) return true;

    // Skip validation if it's a mobile number (these should be caught by the regex)
    if (
      value.startsWith('+336') ||
      value.startsWith('+337') ||
      value.startsWith('+3306') ||
      value.startsWith('+3307')
    ) {
      return true;
    }

    // For all other numbers, ensure minimum length (8 digits + +33 prefix)
    if (value.length < 8 + 3) {
      return createError({
        path,
        message: i18next.t('validation:minLength', { value: 9 }),
      });
    }

    return true;
  });
});

declare module 'yup' {
  interface StringSchema<
    TType extends Maybe<string> = string | undefined,
    TContext extends AnyObject = Yup.AnyObject,
    TDefault = undefined,
    TFlags extends Yup.Flags = '',
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    TOut extends TType = TType,
  > extends Yup.Schema<TType, TContext, TDefault, TFlags> {
    dateFormat(formats, dateSeperator, message): StringSchema<TType, TContext>;
    fullAge(formats, dateSeperator, message): StringSchema<TType, TContext>;
    notInFuture(formats, message): StringSchema<TType, TContext>;
    elderAge(formats, message): StringSchema<TType, TContext>;
    mobilePhoneLength();
    mobilePhoneLengthNoPrefix();
    landlinePhoneLength();
  }
}

export { Yup };
